import React, {useEffect, useState} from 'react';
import {Form, Input, Select} from 'antd';
import {useLazyQuery, useQuery} from '@apollo/client';
import {
    GET_ALL_STATES,
    GET_CITIES_BY_STATE,
    GET_ZIPCODE_DETAIL,
    GET_ZIPCODES_BY_STATE_AND_CITY,
    GetAllStatesResponse,
    GetCitiesByStateResponse,
    GetZipcodeDetailResponse,
    GetZipcodesByStateAndCityResponse
} from '../pages/pickup/appointment/graphql/queries/getLocationData';
import { useTranslation } from 'react-i18next';
    
const { Option } = Select;

interface CascadingAddressSelectorProps {
    // Form field names
    countryFieldName?: string;
    stateFieldName?: string;
    cityFieldName?: string;
    zipcodeFieldName?: string;

    // Placeholders
    countryPlaceholder?: string;
    statePlaceholder?: string;
    cityPlaceholder?: string;
    zipcodePlaceholder?: string;

    // Initial values
    initialCountry?: string;
    initialState?: string;
    initialCity?: string;
    initialZipcode?: string;

    // Styling
    countryWidth?: string;
    stateWidth?: string;
    cityWidth?: string;
    zipcodeWidth?: string;

    // Callbacks
    onStateChange?: (state: string) => void;
    onCityChange?: (city: string) => void;
    onZipcodeChange?: (zipcode: string) => void;

    // Form instance (required for updating values)
    form?: any;

    // Layout
    layout?: 'inline' | 'alignedRow';
    addressLabel?: React.ReactNode;
    labelCol?: { span: number };
    wrapperCol?: { span: number };
    fullWidth?: boolean;
    className?: string;

    // Auto-fill geo coding from zipcode
    autoFillGeoLocation?: boolean;
    geoLocationFieldName?: string;
}

const CascadingAddressSelector: React.FC<CascadingAddressSelectorProps> = ({
    countryFieldName = 'country',
    stateFieldName = 'state',
    cityFieldName = 'city',
    zipcodeFieldName = 'zipcode',
    countryPlaceholder = 'Country',
    statePlaceholder = 'State',
    cityPlaceholder = 'City',
    zipcodePlaceholder = 'Zipcode',
    initialCountry = 'US',
    initialState = undefined,
    initialCity = undefined,
    initialZipcode = undefined,
    countryWidth = '20%',
    stateWidth = '20%',
    cityWidth = '35%',
    zipcodeWidth = '25%',
    onStateChange,
    onCityChange,
    onZipcodeChange,
    form,
    layout = 'inline',
    addressLabel,
    labelCol = { span: 7 },
    wrapperCol = { span: 17 },
    fullWidth = false,
    className,
    autoFillGeoLocation = false,
    geoLocationFieldName = 'location'
}) => {
    const [selectedState, setSelectedState] = useState(initialState);
    const [selectedCity, setSelectedCity] = useState(initialCity);
    const [selectedZipcode, setSelectedZipcode] = useState(initialZipcode);
    const [cities, setCities] = useState<string[]>([]);
    const [zipcodes, setZipcodes] = useState<string[]>([]);
    const { t } = useTranslation();
    // Get all states
    const { data: statesData, loading: statesLoading } = useQuery<GetAllStatesResponse>(GET_ALL_STATES, {
        fetchPolicy: 'cache-and-network'
    });

    // Get cities by state
    const [getCitiesByState, { loading: citiesLoading }] = useLazyQuery<GetCitiesByStateResponse>(GET_CITIES_BY_STATE, {
        fetchPolicy: 'cache-and-network',
        onCompleted: (data) => {
            if (data?.getCitiesByState) {
                setCities(data.getCitiesByState);
            }
        }
    });

    // Get zipcodes by state and city
    const [getZipcodesByStateAndCity, { loading: zipcodesLoading }] = useLazyQuery<GetZipcodesByStateAndCityResponse>(GET_ZIPCODES_BY_STATE_AND_CITY, {
        fetchPolicy: 'cache-and-network',
        onCompleted: (data) => {
            if (data?.getZipcodesByStateAndCity) {
                setZipcodes(data.getZipcodesByStateAndCity);
            }
        }
    });

    // Get zipcode detail for geo location
    const [getZipcodeDetail] = useLazyQuery<GetZipcodeDetailResponse>(GET_ZIPCODE_DETAIL, {
        fetchPolicy: 'cache-and-network',
        onCompleted: (data) => {
            if (data?.getZipcodeByCode) {
                const zipcodeData = data.getZipcodeByCode;
                if (autoFillGeoLocation && form && zipcodeData.latitude && zipcodeData.longitude) {
                    const geoLocation = `${zipcodeData.latitude},${zipcodeData.longitude}`;
                    form.setFieldValue(geoLocationFieldName, geoLocation);
                }
                if (form && zipcodeData.timezone) {
                    form.setFieldValue('timezone', zipcodeData.timezone);
                }
            }
        }
    });

    // Handle state change
    const handleStateChange = (state: string) => {
        setSelectedState(state);
        setSelectedCity('');
        setSelectedZipcode('');
        setCities([]);
        setZipcodes([]);

        // Update form values
        if (form) {
            form.setFieldsValue({
                [stateFieldName]: state,
                [cityFieldName]: undefined,
                [zipcodeFieldName]: undefined,
            });
        }

        // Load cities for the selected state
        if (state) {
            getCitiesByState({ variables: { state } });
        }

        // Call external callback
        onStateChange?.(state);
    };

    // Handle city change
    const handleCityChange = (city: string) => {
        setSelectedCity(city);
        setSelectedZipcode('');
        setZipcodes([]);

        // Update form values
        if (form) {
            form.setFieldsValue({
                [cityFieldName]: city,
                [zipcodeFieldName]: undefined,
            });
        }

        // Load zipcodes for the selected state and city
        if (selectedState && city) {
            getZipcodesByStateAndCity({ variables: { state: selectedState, city } });
        }

        // Call external callback
        onCityChange?.(city);
    };

    // Handle zipcode change
    const handleZipcodeChange = (zipcode: string) => {
        setSelectedZipcode(zipcode);

        // Update form value
        if (form) {
            form.setFieldValue(zipcodeFieldName, zipcode);
        }

        // Auto-fill geo location and timezone if enabled
        if (zipcode) {
            getZipcodeDetail({ variables: { zipcode } });
        }

        // Call external callback
        onZipcodeChange?.(zipcode);
    };

    // Initialize with initial values
    useEffect(() => {
        if (initialState && statesData?.getAllStates?.includes(initialState)) {
            setSelectedState(initialState);
            getCitiesByState({ variables: { state: initialState } });
        }
    }, [initialState, statesData, getCitiesByState]);

    useEffect(() => {
        if (initialCity && selectedState) {
            setSelectedCity(initialCity);
            getZipcodesByStateAndCity({ variables: { state: selectedState, city: initialCity } });
        }
    }, [initialCity, selectedState, getZipcodesByStateAndCity]);

    useEffect(() => {
        if (initialZipcode) {
            setSelectedZipcode(initialZipcode);
        }
    }, [initialZipcode]);

    const selectClassName = fullWidth ? 'flex-1 min-w-0' : undefined;

    const countrySelect = (
        <Form.Item
            name={countryFieldName}
            noStyle
            rules={[{ required: true, message: t('central.sites.messages.pleaseSelectCountry') as string }]}
            initialValue={initialCountry}
        >
            <Select
                style={fullWidth ? undefined : { width: countryWidth }}
                className={selectClassName}
                placeholder={countryPlaceholder}
                disabled
            >
                <Option value="US">{t('central.sites.placeholders.us')}</Option>
            </Select>
        </Form.Item>
    );

    const stateSelect = (
        <Form.Item
            name={stateFieldName}
            noStyle
            rules={[{ required: true, message: t('central.sites.messages.pleaseSelectState') as string }]}
            initialValue={initialState}
        >
            <Select
                style={fullWidth ? undefined : { width: stateWidth }}
                className={selectClassName}
                placeholder={statePlaceholder}
                loading={statesLoading}
                onChange={handleStateChange}
                value={selectedState}
                showSearch
                filterOption={(input, option) =>
                    (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                }
            >
                {statesData?.getAllStates?.map((state) => (
                    <Option key={state} value={state}>
                        {state}
                    </Option>
                ))}
            </Select>
        </Form.Item>
    );

    const citySelect = (
        <Form.Item
            name={cityFieldName}
            noStyle
            rules={[{ required: true, message: t('central.sites.messages.pleaseSelectCity') as string }]}
            initialValue={initialCity}
        >
            <Select
                style={fullWidth ? undefined : { width: cityWidth }}
                className={selectClassName}
                placeholder={cityPlaceholder}
                loading={citiesLoading}
                onChange={handleCityChange}
                value={selectedCity}
                disabled={!selectedState}
                showSearch
                filterOption={(input, option) =>
                    (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                }
            >
                {cities.map((city) => (
                    <Option key={city} value={city}>
                        {city}
                    </Option>
                ))}
            </Select>
        </Form.Item>
    );

    const zipcodeSelect = (
        <Form.Item
            name={zipcodeFieldName}
            noStyle
            rules={[{ required: true, message: t('central.sites.messages.pleaseSelectZipcode') as string }]}
            initialValue={initialZipcode}
        >
            <Select
                style={fullWidth ? undefined : { width: zipcodeWidth }}
                className={selectClassName}
                placeholder={zipcodePlaceholder}
                loading={zipcodesLoading}
                onChange={handleZipcodeChange}
                value={selectedZipcode}
                disabled={!selectedCity}
                showSearch
                filterOption={(input, option) =>
                    (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                }
            >
                {zipcodes.map((zipcode) => (
                    <Option key={zipcode} value={zipcode}>
                        {zipcode}
                    </Option>
                ))}
            </Select>
        </Form.Item>
    );

    if (layout === 'alignedRow') {
        const alignedGridClassName =
            'grid w-full items-start grid-cols-1 lg:[grid-template-columns:calc((100%-2.5rem)*7/48)_calc((100%-2.5rem)*17/48)_2.5rem_calc((100%-2.5rem)*7/48)_calc((100%-2.5rem)*17/48)]';

        return (
            <div className={alignedGridClassName}>
                <div className="ant-form-item-label text-left lg:pt-1">
                    <label className="ant-form-item-required">
                        {addressLabel}
                    </label>
                </div>
                <div className="min-w-0 lg:col-start-2 lg:col-end-[-1]">
                    <Input.Group
                        compact
                        className={`flex w-full [&_.ant-select]:!flex-shrink-0 ${className ?? ''}`}
                    >
                        {countrySelect}
                        {stateSelect}
                        {citySelect}
                        {zipcodeSelect}
                    </Input.Group>
                </div>
            </div>
        );
    }

    if (fullWidth) {
        return (
            <div className={`flex w-full gap-2 ${className ?? ''}`}>
                {countrySelect}
                {stateSelect}
                {citySelect}
                {zipcodeSelect}
            </div>
        );
    }

    return (
        <Input.Group compact className={className}>
            {countrySelect}
            {stateSelect}
            {citySelect}
            {zipcodeSelect}
        </Input.Group>
    );
};

export default CascadingAddressSelector;